import { Stack } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import { usePortfoliosQuery } from '@/features/bidding/hooks/usePortfoliosQuery'
import type { Portfolio } from '@/features/bidding/types/bid'
import { PortfolioState } from '@/features/bidding/types/bid'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'
import PortfoliosDataGridToolbar from '@/features/portfolioHierarchy/components/PortfoliosDataGridToolbar'
import PortfolioStateChip from '@/features/portfolioHierarchy/components/PortfolioStateChip'

function PortfoliosDataGrid() {
  const { t } = useTranslation()
  const { isLoading, portfolios } = usePortfoliosQuery()

  const columns: GridColDef<Portfolio>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('portfolio_hierarchy.portfolio.name'),
        flex: 3,
      },
      {
        field: 'code',
        headerName: t('portfolio_hierarchy.portfolio.code'),
        flex: 3,
      },
      {
        field: 'countryCode',
        headerName: t('portfolio_hierarchy.portfolio.country_code'),
        flex: 2,
        renderCell: (params) => {
          return (
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', height: '100%', whiteSpace: 'nowrap' }}>
              <FlagIcon countryCode={params.row.countryCode?.toLowerCase() as CountryIdentifier} />
              <span style={{ marginLeft: 4 }}>
                {t(`common.country.${params.value.toLowerCase() as CountryIdentifier}`)}
              </span>
            </Stack>
          )
        },
      },
      {
        field: 'priceArea',
        headerName: t('portfolio_hierarchy.portfolio.price_area'),
        flex: 2,
      },
      {
        field: 'partnerCode',
        headerName: t('portfolio_hierarchy.partner.code'),
        flex: 2,
      },
      {
        field: 'partnerName',
        headerName: t('portfolio_hierarchy.partner.name'),
        flex: 3,
      },
      {
        field: 'partition',
        headerName: t('portfolio_hierarchy.portfolio.partition'),
        flex: 2,
      },
      {
        field: 'state',
        headerName: t('portfolio_hierarchy.portfolio.state'),
        flex: 2,
        renderCell: (params) => {
          if (!Object.values(PortfolioState).includes(params.value as PortfolioState)) {
            const unknownStateMessage = `Unknown portfolio state from backend: "${params.value}". Please update PortfolioState enum.`
            logMessageInCoralogix(unknownStateMessage)
            return params.value
          }

          return <PortfolioStateChip state={params.value} />
        },
      },
    ],
    [t],
  )

  return (
    <CustomDataGrid
      columns={columns}
      getRowId={(row: Portfolio) => row.code}
      initialState={{
        sorting: {
          sortModel: [
            { field: 'countryCode', sort: 'asc' },
            { field: 'code', sort: 'asc' },
          ],
        },
      }}
      isLoading={isLoading}
      rows={portfolios ?? []}
      slotProps={{
        root: {
          'data-testid': 'portfolio-grid',
        },
        toolbar: {
          portfolios,
        },
      }}
      slots={{ toolbar: PortfoliosDataGridToolbar }}
    />
  )
}

export default PortfoliosDataGrid
